import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay } from "@fortawesome/free-solid-svg-icons";
import { annotateMessageRuns, clockTime } from "../../utils/timeFormat";
import { PreviewImage } from "../../pages/previewImage";

// The message list.
//
// Spec, all of it load-bearing:
//   • Messages anchor to the BOTTOM. Empty space goes above the first message,
//     never below the last, so a short thread sits low on the screen.
//   • Auto-scroll to newest on open and on receive.
//   • A date separator labels every message below it until the next separator.
//   • Avatar renders on the FIRST bubble of a consecutive incoming run only.
//   • Clock time renders under the LAST bubble of a run only — clock-only,
//     never a date (the separator already supplies that).
//   • Incoming #F0F2F2 / black, outgoing #004C50 / white.
//   • Outgoing timestamps right-align, incoming left-align, both secondary grey.
//
// Bubble max width differs by surface (60% of the desktop panel, 75% of the
// mobile screen), so it comes in as a prop rather than being hard-coded.
export default function ChatThread({
  messages,
  myId,
  maxBubbleClass = "max-w-[75%]",
  className = "",
}) {
  const scrollRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [playing, setPlaying] = useState({});

  // Newest at the bottom, in view — on open and on every receive. Driven by
  // scrollTop on our OWN container rather than scrollIntoView, which would also
  // scroll the page behind the panel.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const rows = annotateMessageRuns(messages, (m) => m?.user_id);

  return (
    <div ref={scrollRef} className={`flex flex-col overflow-y-auto ${className}`}>
      {/* mt-auto is what anchors the run to the bottom: the spacer above
          absorbs the slack, so short threads sit low instead of floating at the
          top of an empty panel. */}
      <div className="mt-auto px-4 pb-2 pt-4">
        {rows.map(({ message, date, separator, isRunStart, isRunEnd }, i) => {
          const outgoing = String(message?.user_id) === String(myId);
          const isVideo = String(message?.type) === "3";
          return (
            <React.Fragment key={message?.id ?? i}>
              {separator && (
                <div className="my-4 flex items-center gap-3">
                  <span className="h-px flex-1 bg-divider" />
                  <span className="text-xs text-secondary">{separator}</span>
                  <span className="h-px flex-1 bg-divider" />
                </div>
              )}

              <div
                className={`flex items-end gap-2 ${
                  outgoing ? "justify-end" : "justify-start"
                } ${isRunEnd ? "mb-3" : "mb-1"}`}
              >
                {/* Avatar slot is always reserved so bubbles in a run stay in
                    one column; only the first incoming bubble fills it. */}
                {!outgoing && (
                  <div className="h-8 w-8 flex-shrink-0">
                    {isRunStart && (
                      <img
                        src={message?.user?.image}
                        alt=""
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    )}
                  </div>
                )}

                <div className={`flex min-w-0 flex-col ${maxBubbleClass}`}>
                  {message?.attachments ? (
                    isVideo ? (
                      <div className="relative overflow-hidden rounded-2xl">
                        {playing[i] ? (
                          <video
                            controls
                            autoPlay
                            playsInline
                            controlsList="nodownload"
                            disablePictureInPicture
                            onContextMenu={(e) => e.preventDefault()}
                            className="max-h-64 w-full rounded-2xl object-cover"
                          >
                            <source src={message.attachments} />
                          </video>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              setPlaying((p) => ({ ...p, [i]: true }))
                            }
                            className="relative block w-full"
                          >
                            <img
                              src={`${message.attachments}.jpg`}
                              alt=""
                              className="max-h-64 w-full rounded-2xl object-cover"
                            />
                            <span className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black bg-opacity-40 text-white">
                              <FontAwesomeIcon icon={faPlay} className="h-6" />
                            </span>
                          </button>
                        )}
                      </div>
                    ) : (
                      <img
                        src={message.attachments}
                        alt=""
                        onClick={() => setPreview(message.attachments)}
                        className="max-h-72 cursor-pointer rounded-2xl object-cover"
                      />
                    )
                  ) : null}

                  {message?.message && (
                    <span
                      className={`inline-block break-words rounded-2xl px-4 py-2 text-sm ${
                        outgoing
                          ? "bg-primaryColor text-white"
                          : "bg-[#F0F2F2] text-black"
                      } ${message?.attachments ? "mt-1" : ""}`}
                    >
                      {message.message}
                    </span>
                  )}

                  {/* Clock time on the last bubble of a run only. */}
                  {isRunEnd && date && (
                    <span
                      className={`mt-1 text-xs text-secondary ${
                        outgoing ? "text-right" : "text-left"
                      }`}
                    >
                      {clockTime(date)}
                    </span>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {preview && (
        <PreviewImage src={preview} onClick={() => setPreview(null)} />
      )}
    </div>
  );
}
